import { cacheKeyFor, type Store } from '@warp-drive/core';
import type {
  CollectionRelationship,
  ResourceRelationship,
} from '@warp-drive/core/types/cache/relationship';
import type { ResourceKey } from '@warp-drive/core/types/identifier';

/**
 * Options for controlling the behavior of deepRollback.
 */
export interface DeepRollbackOptions {
  /**
   * Maximum depth to traverse relationships. Prevents infinite recursion in deeply nested structures.
   * Default: Infinity
   */
  maxDepth?: number;

  /**
   * Internal tracking of visited records to prevent circular reference loops.
   * Should not be set by external callers.
   * @internal
   */
  visited?: Set<string>;

  /**
   * Prevent the root record from being unloaded if new
   */
  preventNewRecordUnload?: boolean;
}

/**
 * Creates a unique identifier for a record based on its cache key.
 */
function getCacheKeyId(cacheKey: ResourceKey): string {
  return `${cacheKey.type}:${cacheKey.id ?? 'null'}`;
}

/**
 * Deeply rolls back changes to a record and all its related records.
 *
 * This function:
 * 1. Traverses all relationships
 * 2. Recursively rolls back related records
 * 3. Rolls back the record's relationships and attributes
 * 4. Unloads new records that were created but not saved
 *
 * @param record - The record to roll back
 * @param store - The Warp Drive store instance
 * @param options - Optional configuration for rollback behavior
 *
 * @example
 * ```typescript
 * // Simple rollback
 * deepRollback(cohort, this.store);
 *
 * // Limit depth to avoid deep traversal
 * deepRollback(cohort, this.store, { maxDepth: 2 });
 * ```
 */
export function deepRollback(
  record: unknown,
  store: Store,
  options: DeepRollbackOptions = {}
): void {
  const {
    maxDepth = Infinity,
    visited = new Set<string>(),
    preventNewRecordUnload = false,
  } = options;

  // Early return if we've reached max depth (depth 0 means we've exhausted our limit)
  if (maxDepth <= 0) {
    return;
  }

  // Get the cache key for this record
  const cacheKey = cacheKeyFor(record);
  const cacheKeyId = getCacheKeyId(cacheKey);

  // Prevent circular references
  if (visited.has(cacheKeyId)) {
    return;
  }
  visited.add(cacheKeyId);

  // Get all fields from the schema
  const fields = store.schema.fields(cacheKey);

  // Get changed relationships
  const changedRelationships = store.cache.changedRelationships(cacheKey);
  const changedAttrs = store.cache.changedAttrs(cacheKey);

  console.log('changedRelationships', changedRelationships);
  console.log('changedAttrs', changedAttrs);

  // Process each relationship
  for (const [fieldName, field] of fields.entries()) {
    if (field.kind === 'schema-array') {
      const changedArrayAttrs = changedAttrs[fieldName];
      if (!changedArrayAttrs) {
        console.warn(`No changed array attributes for field ${fieldName}`);
        continue;
      }
      if (changedArrayAttrs[0]) {
        console.warn(`No old value for changed array attribute ${fieldName}`);
        continue;
      } else {
        console.log(
          'old value for changed array attribute',
          changedArrayAttrs[0]
        );
      }

      console.log('field is schema-array', field);
    }

    // Only process relationship fields
    if (field.kind !== 'belongsTo' && field.kind !== 'hasMany') {
      continue;
    }

    // Collect all related record keys
    const relatedKeys = new Set<ResourceKey>();

    const relationshipDiff = changedRelationships.get(fieldName);
    if (!relationshipDiff) {
      // Relationship hasn't changed, but we still need to traverse it
      // to check for dirty records nested deeper
      if (field.kind === 'hasMany') {
        const relationship = store.cache.getRelationship(
          cacheKey,
          fieldName,
          true
        ) as CollectionRelationship;
        if (relationship.data) {
          for (const relatedItemKey of relationship.data) {
            // Add all records for traversal, not just dirty ones
            // This ensures we can find dirty records nested deeper
            relatedKeys.add(relatedItemKey);
          }
        }
      } else if (field.kind === 'belongsTo') {
        const relationship = store.cache.getRelationship(
          cacheKey,
          fieldName,
          false
        ) as ResourceRelationship;
        if (relationship.data) {
          const relatedItemKey = relationship.data;
          // Add all records for traversal, not just dirty ones
          relatedKeys.add(relatedItemKey);
        }
      }
    } else {
      if (relationshipDiff.kind === 'collection') {
        // hasMany relationship
        // Add all records from remote state (original)
        for (const key of relationshipDiff.remoteState) {
          relatedKeys.add(key);
        }
        // Add all records from local state (current)
        for (const key of relationshipDiff.localState) {
          relatedKeys.add(key);
        }
        // Add all additions
        for (const key of relationshipDiff.additions) {
          relatedKeys.add(key);
        }
        // Add all removals
        for (const key of relationshipDiff.removals) {
          relatedKeys.add(key);
        }
      } else {
        // belongsTo relationship
        if (relationshipDiff.remoteState) {
          relatedKeys.add(relationshipDiff.remoteState);
        }
        if (relationshipDiff.localState) {
          relatedKeys.add(relationshipDiff.localState);
        }
      }
    }

    // Recursively rollback each related record
    for (const relatedKey of relatedKeys) {
      try {
        const relatedRecord = store.peekRecord(relatedKey);
        if (relatedRecord) {
          deepRollback(relatedRecord, store, {
            maxDepth: maxDepth - 1,
            visited,
            preventNewRecordUnload: false,
          });
        }
      } catch (error) {
        // If we can't peek the record, it may have been unloaded
        // Continue processing other records
        console.warn(
          `Could not peek record ${relatedKey.type}:${relatedKey.id}`,
          error
        );
      }
    }
  }

  // Unload new records instead of rolling back
  if (
    typeof record === 'object' &&
    record !== null &&
    'isNew' in record &&
    record.isNew === true &&
    !preventNewRecordUnload
  ) {
    try {
      store.unloadRecord(record);
    } catch (error) {
      console.warn(`Could not unload new record ${cacheKeyId}`, error);
    }
  } else {
    // Roll back this record's relationships and attributes
    store.cache.rollbackRelationships(cacheKey);
    store.cache.rollbackAttrs(cacheKey);
  }
}
